import { BRAND } from '@/lib/brand';
/**
 * A person who opted out of data enrichment. The backend refuses to enrich
 * them and charges nothing (cleanse-api `utils/data_suppression.py`), so the
 * portal shows that as its own outcome: never a failure to retry, a miss, or
 * something to reveal or pay for.
 */
export const PERSON_OPTED_OUT = 'person_opted_out';

/** The backend's refusal message, word for word. */
export const OPTED_OUT_MESSAGE =
  "Can't enrich this lead: this person opted out of data enrichment.";

export const OPTED_OUT_TOOLTIP = `This person opted out of data enrichment. ${BRAND.name} won't find or return their contact details, and you weren't charged.`;

/** People search's revealed-value stand-in for an opted-out person (as '—'
 *  stands for "not found"); no email or phone can equal it. */
export const OPTED_OUT_REVEAL = 'opted_out';

// A list row stores the refusal only as its error message, so rows are
// recognized by its opening words.
const OPTED_OUT_PREFIX = "Can't enrich this lead: this person opted out";

export const isOptedOutMessage = (message?: string | null): boolean =>
  typeof message === 'string' && message.trim().startsWith(OPTED_OUT_PREFIX);

/** A websocket frame or task status: `failure_kind` when sent, else the
 *  message. */
export const isOptedOutFailure = (
  failure?: {
    failure_kind?: string | null;
    error?: string | null;
    message?: string | null;
  } | null
): boolean =>
  failure?.failure_kind === PERSON_OPTED_OUT ||
  isOptedOutMessage(failure?.error) ||
  isOptedOutMessage(failure?.message);

/**
 * The message of an enrich call the API refused because the person opted out
 * (HTTP 422, `detail = {code: "person_opted_out", message}`), else null. Reads
 * both the error envelope (`error.details.code`) and FastAPI's raw `detail`.
 */
export function optedOutErrorMessage(error: unknown): string | null {
  const data = (error as { data?: unknown } | null)?.data;
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const envelope = (
    typeof record.error === 'object' && record.error ? record.error : {}
  ) as Record<string, unknown>;
  const details = [envelope.details, record.detail, envelope].find(
    (value): value is Record<string, unknown> =>
      typeof value === 'object' &&
      value !== null &&
      String((value as { code?: unknown }).code).toLowerCase() ===
        PERSON_OPTED_OUT
  );
  if (!details) return null;
  const message = [details.message, envelope.message].find(
    (value): value is string => isOptedOutMessage(value as string)
  );
  return message ?? OPTED_OUT_MESSAGE;
}
